import React from "react";
import { useAppSelector } from "../../../../../../../hooks/redux";

import { formatValue, FormatValueTypes, getDisplayType } from "./helpers";
import { selectTaskStatus } from "../../../../../../../slices/taskStatus";
import { selectTaskType } from "../../../../../../../slices/taskType";
import { selectTaskPriority } from "../../../../../../../slices/priority";
import { selectCategory } from "@SonarCX/slices/category";
import { selectUser } from "@SonarCX/slices/user";
import { selectTeams } from "@SonarCX/slices/teamsSlice";
import { selectCustomFields } from "@SonarCX/slices/customFields";

type TaskUpdateProps = {
  type: string;
  oldValue: any;
  newValue: any;
  name: string;
};

enum ChangeTypes {
  SET_NEW,
  REMOVE,
  UPDATE,
}

const TaskUpdate: React.FC<TaskUpdateProps> = ({
  type,
  oldValue,
  newValue,
  name,
}) => {
  const { statuses } = useAppSelector(selectTaskStatus);
  const { types } = useAppSelector(selectTaskType);
  const { priorities } = useAppSelector(selectTaskPriority);
  const { categories } = useAppSelector(selectCategory);
  const { users } = useAppSelector(selectUser);
  const { teams } = useAppSelector(selectTeams);
  const { customFields } = useAppSelector(selectCustomFields);

  const changeType = React.useMemo(() => {
    if (newValue && !oldValue) {
      return ChangeTypes.SET_NEW;
    }

    if (!newValue && oldValue) {
      return ChangeTypes.REMOVE;
    }

    return ChangeTypes.UPDATE;
  }, [oldValue, newValue]);

  const displayType = React.useMemo(
    () => getDisplayType({ type, customFields }),
    [type, customFields],
  );

  const [oldDisplayVal, newDisplayVal] = React.useMemo(() => {
    const data: Omit<FormatValueTypes, "val"> = {
      type,
      statuses,
      types,
      priorities,
      categories,
      users,
      teams,
      customFields,
    };
    const oldVal = formatValue({ val: oldValue, ...data });
    const newVal = formatValue({ val: newValue, ...data });

    return [oldVal, newVal];
  }, [
    oldValue,
    newValue,
    type,
    statuses,
    types,
    priorities,
    categories,
    users,
    teams,
    customFields,
  ]);

  if (displayType === null) {
    return null;
  }

  if (changeType === ChangeTypes.SET_NEW) {
    return (
      <>
        {name} <span className="emphasize">set</span> {displayType} to{" "}
        <span className="emphasize">{newDisplayVal}</span>
      </>
    );
  }

  if (changeType === ChangeTypes.REMOVE) {
    return (
      <>
        {name} <span className="emphasize">unset</span> {displayType}, was{" "}
        <span className="emphasize">{oldDisplayVal}</span>
      </>
    );
  }

  if (["assigneeIds", "watcherIds"].includes(type)) {
    if (!newDisplayVal || newDisplayVal.length === 0) {
      return (
        <>
          {name} <span className="emphasize">removed all</span> {displayType}
        </>
      );
    }

    return (
      <>
        {name} <span className="emphasize">set</span> {displayType} to{" "}
        <span className="emphasize">{newDisplayVal}</span>
      </>
    );
  }

  return (
    <>
      {name} <span className="emphasize">changed</span> {displayType} from{" "}
      <span className="emphasize">{oldDisplayVal}</span> to{" "}
      <span className="emphasize">{newDisplayVal}</span>
    </>
  );
};

export default TaskUpdate;
